import React, { useState, useEffect } from "react";
import {
    Users, Building2, CreditCard, Activity, TrendingUp,
    AlertTriangle, CheckCircle2, Zap, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import api from "../api/axiosInstance";

const SaasDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("get_saas_metrics.php").then(res => {
            setData(res.data);
            setLoading(false);
        });
    }, []);

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-300">CALCULATING ECOSYSTEM ANALYTICS...</div>;

    const { metrics, recent_tenants } = data;

    const StatCard = ({ title, value, icon, trend, trendValue, color }) => (
        <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm hover:shadow-xl transition-all">
            <div className="flex justify-between items-start mb-4">
                <div className={`p-4 rounded-2xl ${color} bg-opacity-10 text-${color.split('-')[1]}-600`}>
                    {icon}
                </div>
                {trend && (
                    <div className={`flex items-center gap-1 text-[10px] font-black px-3 py-1 rounded-full ${trend === 'up' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                        {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {trendValue}
                    </div>
                )}
            </div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</p>
            <h3 className="text-3xl font-black italic tracking-tighter text-slate-900 mt-1">
                {typeof value === 'number' && title.includes('Volume') ? `₹${value.toLocaleString()}` : value}
            </h3>
        </div>
    );

    return (
        <div className="p-8 space-y-8 bg-slate-50/50 min-h-screen pb-20">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-slate-900">Infrastructure Overview</h1>
                    <p className="text-[10px] font-black uppercase text-indigo-500 tracking-widest mt-1">Real-time Platform Intelligence</p>
                </div>
                <div className="flex gap-2 bg-white p-2 rounded-2xl border border-slate-100 shadow-sm">
                    <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div> API: Operational
                    </div>
                </div>
            </div>

            {/* Primary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Active Tenants" value={metrics.active_tenants} icon={<Building2 size={24} />} trend="up" trendValue="12%" color="bg-indigo-500" />
                <StatCard title="New Users (30d)" value={metrics.new_users_30d} icon={<Users size={24} />} trend="up" trendValue="8%" color="bg-blue-500" />
                <StatCard title="Platform Activity" value={metrics.activity_24h} icon={<Zap size={24} />} trend="up" trendValue="24%" color="bg-amber-500" />
                <StatCard title="Invoiced Volume" value={metrics.monthly_invoice_volume} icon={<CreditCard size={24} />} color="bg-emerald-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Onboarding */}
                <div className="lg:col-span-2 bg-white rounded-[40px] p-10 border border-slate-100 shadow-sm">
                    <div className="flex justify-between items-center mb-8">
                        <h3 className="text-xl font-black italic tracking-tighter uppercase text-slate-900">Recent Registrations</h3>
                        <button className="text-[10px] font-black text-indigo-600 uppercase border-b-2 border-indigo-600">View All Tenants</button>
                    </div>
                    <div className="space-y-4">
                        {recent_tenants.map((tenant, i) => (
                            <div key={i} className="flex items-center justify-between p-6 bg-slate-50 rounded-[32px] border border-transparent hover:border-indigo-100 hover:bg-white transition-all">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center font-black text-indigo-600 shadow-sm uppercase">
                                        {tenant.company_name[0]}
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-900 uppercase italic">{tenant.company_name}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase">Joined {new Date(tenant.created_at).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase ${tenant.is_active ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                                    {tenant.is_active ? 'Active' : 'Pending'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* System Health / Warnings */}
                <div className="bg-slate-900 rounded-[40px] p-10 text-white shadow-2xl space-y-8">
                    <h3 className="text-xl font-black italic tracking-tighter uppercase text-indigo-400">Critical Alerts</h3>
                    <div className="space-y-6">
                        <div className="flex gap-4 p-5 bg-white/5 rounded-3xl border border-white/10">
                            <AlertTriangle className="text-amber-500 shrink-0" size={20} />
                            <div>
                                <p className="text-xs font-black uppercase">Expired Subscriptions</p>
                                <p className="text-[10px] text-slate-400 mt-1 font-bold">{metrics.expired_tenants} companies currently locked out.</p>
                            </div>
                        </div>
                        <div className="flex gap-4 p-5 bg-white/5 rounded-3xl border border-white/10">
                            <CheckCircle2 className="text-emerald-500 shrink-0" size={20} />
                            <div>
                                <p className="text-xs font-black uppercase">Database Integrity</p>
                                <p className="text-[10px] text-slate-400 mt-1 font-bold">100% Sync across {metrics.total_tenants} tenants.</p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-10 border-t border-white/10">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Infrastructure Health</p>
                        <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 w-[98%]"></div>
                        </div>
                        <p className="text-right text-[9px] font-black text-indigo-400 mt-2">98% SERVER LOAD</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SaasDashboard;